import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Select,
  MenuItem,
  FormControl,
  TextField,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  withStyles,
  Divider,
} from "@material-ui/core";
import axios from "axios";
import EditIcon from "@material-ui/icons/Edit";
import { Check } from "@material-ui/icons";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import CloseIcon from "@material-ui/icons/Close";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";
import { appUrl } from "../../../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  iosSwitch: {
    "& > *": {
      height: "22px !important",
    },
    "& .MuiSwitch-switchBase .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "& .MuiSwitch-switchBase": {
      left: "2px",
      top: "-1px",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      left: "auto",
      right: "18px",
    },
    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track": {
      backgroundColor: "#1890ff !important",
    },
  },
  checkedSwitch: {
    position: "absolute",
    width: "16px !important",
    left: "12px",
    top: "-3px",
    color: "white",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  actionButtons: {
    display: "flex",
    flexDirection: "column",
    marginTop: 4,
  },
  mandatoryDropdown: {
    width: '100%', // This will make it take full available width
    "& .MuiSelect-root": {
      fontSize: '14px',
      padding: '8px 12px',
      textAlign: 'left',
    },
    "& .MuiSelect-icon": {
      right: '8px',
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: '#c0c0c0',
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: '#a0a0a0',
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: '#1890ff',
    },
    "& .Mui-disabled": {
    color: "rgba(0, 0, 0, 0.87) !important", // text color same as enabled
    "-webkit-text-fill-color": "rgba(0, 0, 0, 0.87) !important", // safari fix
    },
    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c0c0c0 !important", // keep border visible
    },
  },
}));

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    disabled={props.disabled}
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

const DocumentTypesSection = forwardRef(
  (
    {
      documentTypesData: initialDocumentTypesData = [],
      isDocLoading: initialIsDocLoading = false,
      editMode: initialEditMode = false,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      onChange,
      disableEdit = false,
    },
    ref
  ) => {
    const classes = useStyles();
    const [documentTypesData, setDocumentTypesData] = useState([
      ...initialDocumentTypesData,
    ]);
    const [isDocLoading, setIsDocLoading] = useState(initialIsDocLoading);
    const [editMode, setEditMode] = useState(initialEditMode);
    const [editingDocId, setEditingDocId] = useState(null);
    const [editingDocName, setEditingDocName] = useState("");
    const [isModified, setIsModified] = useState(false);
    const [originalDocumentTypesData, setOriginalDocumentTypesData] = useState([
      ...initialDocumentTypesData,
    ]);
    const [categories, setCategories] = useState([]);
const [isCategoryLoading, setIsCategoryLoading] = useState(false);
const API_BASE_URL = useApiBaseUrl(appUrl);
const token = localStorage.getItem('authToken');
    // Update state when props change
    useEffect(() => {
      setDocumentTypesData([...initialDocumentTypesData]);
      setOriginalDocumentTypesData([...initialDocumentTypesData]);
      setIsDocLoading(initialIsDocLoading);
      setEditMode(initialEditMode);
      setIsModified(false);
      setEditingDocId(null);
      setEditingDocName("");
    }, [initialDocumentTypesData, initialIsDocLoading, initialEditMode]);

    // Check if data has been modified
    useEffect(() => {
      const hasChanged =
        JSON.stringify(documentTypesData) !==
        JSON.stringify(originalDocumentTypesData);
      if (isModified !== hasChanged) {
        setIsModified(hasChanged);
      }
    }, [documentTypesData, originalDocumentTypesData, isModified]);


    // Fetch document types when entering edit mode
    useEffect(() => {
      let isMounted = true;
      const fetchDocumentTypes = async () => {
        if (editMode && !isDocLoading) {
          setIsDocLoading(true);
          try {
            const productConfig = true;
            const response = await axios.get(`${API_BASE_URL}/fetchDocumentTypes/${productConfig}`);
            if (response.data.data.documentTypesList && isMounted) {
              const fetchedData = response.data.data.documentTypesList.map(doc => ({
                documentTypeID: doc.documentTypeID,
                documentTypeName: doc.documentTypeName,
                activated: false,
                newDocumentTypeName: doc.newDocumentTypeName || "NA",
                documentType: doc.documentType,
                documentCategoryID: doc.documentCategoryID,
                documentCategoryName: doc.documentCategoryName || "NA",
                mandatoryInProcessWorkflow: doc.mandatoryInProcessWorkflow || false  
              }));

              // Merge fetched data with existing data by documentTypeID while preserving original order
              const mergedData = documentTypesData.map(existingDoc => {
                const fetchedDoc = fetchedData.find(fetched => 
                  fetched.documentTypeID === existingDoc.documentTypeID
                );
                
                if (fetchedDoc) {
                  // Update with fresh data but preserve user modifications and original order
                  return {
                    ...fetchedDoc,
                    activated: existingDoc.activated,
                    newDocumentTypeName: existingDoc.newDocumentTypeName !== "NA" ? existingDoc.newDocumentTypeName : fetchedDoc.newDocumentTypeName,
                    mandatoryInProcessWorkflow: existingDoc.mandatoryInProcessWorkflow
                  };
                }
                
                // If document not found in fetched data, keep the existing one
                return existingDoc;
              });

              // Add any new documents from fetched data that weren't in existing data
              const existingIds = documentTypesData.map(doc => doc.documentTypeID);
              const newDocuments = fetchedData.filter(fetchedDoc => 
                !existingIds.includes(fetchedDoc.documentTypeID)
              );

              const finalMergedData = [...mergedData, ...newDocuments];

              setDocumentTypesData(finalMergedData);
              setOriginalDocumentTypesData(finalMergedData);
            }
          } catch (error) {
            console.error("Error fetching document types:", error);
          } finally {
            if (isMounted) setIsDocLoading(false);
          }
        }
      };
      
      fetchDocumentTypes();
      return () => {
        isMounted = false;
      };
    }, [editMode, API_BASE_URL, token]);

    // useEffect(() => {
    //   let isMounted = true;
    //   const fetchCategories = async () => {
    //     if (!token) {
    //       console.error('No authentication token found');
    //       setCategories([]);
    //       setIsCategoryLoading(false);
    //       return;
    //     }
    //     try {
    //       setIsCategoryLoading(true);
    //       const productConfig = true;
    //       const response = await axios.get(`${API_BASE_URL}/fetchDocumentCategories/${productConfig}`);
    //       if (response.data.documentCategoryList && isMounted) {
    //         setCategories(response.data.documentCategoryList);
    //       }
    //     } catch (error) {
    //       console.error('Error fetching document categories:', error.response?.data || error.message);
    //       setCategories([]);
    //     } finally {
    //       if (isMounted) setIsCategoryLoading(false);
    //     }
    //   };
    //   fetchCategories();
    //   return () => {
    //     isMounted = false;
    //   };
    // }, [API_BASE_URL, token]);

    // useEffect(() => {
    //   let isMounted = true;
    //   const fetchDocumentTypes = async () => {
    //     if (isMounted) setIsDocLoading(true);
    //     try {
    //       const productConfig = true;
    //       const response = await axios.get(`${API_BASE_URL}/fetchDocumentTypes/${productConfig}`);
    //       if (response.data.data.documentTypesList) {
    //         const defaultData = response.data.data.documentTypesList.map(doc => ({
    //           documentTypeID: doc.documentTypeID,
    //           documentTypeName: doc.documentTypeName,
    //           activated: doc.activated,
    //           newDocumentTypeName: doc.newDocumentTypeName || "NA",
    //           documentType: doc.documentType,
    //           documentCategoryID: doc.documentCategoryID,
    //         }));
    //         setDocumentTypesData(defaultData);
    //       }
    //     } catch (error) {
    //       console.error("Error fetching document types:", error);
    //     } finally {
    //       if (isMounted) setIsDocLoading(false);
    //     }
    //   };
    //   fetchDocumentTypes();
    //   return () => {
    //     isMounted = false;
    //   };
    // }, [API_BASE_URL]);

    // const getCategoryName = (categoryId) => {
    //   const category = categories.find(cat => cat.documentCategoryID === categoryId);
    //   return category ? category.documentCategoryName : 'N/A';
    // };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => {

        return documentTypesData;
      },
      getPayload: () => {
        // Only include documents that have been modified
        const modifiedDocuments = documentTypesData.filter(doc => {
          const originalDoc = originalDocumentTypesData.find(orig => 
            orig.documentTypeID === doc.documentTypeID
          );
          
          // Check if document is modified (activated changed or newDocumentTypeName changed)
          return !originalDoc || 
                 originalDoc.activated !== doc.activated ||
                 originalDoc.newDocumentTypeName !== doc.newDocumentTypeName ||
                 originalDoc.mandatoryInProcessWorkflow  !== doc.mandatoryInProcessWorkflow ;
        });
        const payload = {
          vendorMosymphonyID,
          brandOwnerMosymphonyID,
          vendorPlantDetails: {
            cmoDocumentTypesList: modifiedDocuments.map(doc => ({
              documentTypeID: doc.documentTypeID,
              documentTypeName: doc.documentTypeName?.trim() || doc.documentTypeName,
              activated: doc.activated,
              newDocumentTypeName: doc.newDocumentTypeName === "NA" ? null : (doc.newDocumentTypeName?.trim() || doc.newDocumentTypeName),
              documentType: doc.documentType,
              documentCategoryID: doc.documentCategoryID,
              mandatoryInProcessWorkflow: doc.mandatoryInProcessWorkflow
            })),
          },
          updateCMODocumentType: true,
        };

        return payload;
      },
      validateForm: () => true, // Document types don't require validation
      resetForm: () => {

        setDocumentTypesData([...originalDocumentTypesData]);
        setIsModified(false);
        setEditingDocId(null);
        setEditingDocName("");
      },
    }));

    const handleToggle = (id, checked) => {
      setDocumentTypesData((prev) =>
        prev.map((doc) =>
          doc.documentTypeID === id ? { ...doc, activated: checked } : doc
        )
      );

      // Notify parent about change
      if (onChange) {
        onChange();
      }
    };

    const handleMandatoryChange = (id, value) => {
      setDocumentTypesData((prev) =>
        prev.map((doc) =>
          doc.documentTypeID === id ? { ...doc, mandatoryInProcessWorkflow: value === 'Yes' } : doc
        )
      );

      // Notify parent about change
      if (onChange) {
        onChange();
      }
    };


    const handleEditClick = (id, name) => {
      setEditingDocId(id);

      // If name is "NA" or null/undefined, set an empty string in the input field
      // Otherwise use the provided name
      const displayName = name === "NA" || !name ? "" : name;

      setEditingDocName(displayName);
    };

    const handleCancelEdit = () => {
      setEditingDocId(null);
      setEditingDocName("");
    };

    const handleSaveDocType = (id) => {
      setDocumentTypesData((prev) =>
        prev.map((doc) =>
          doc.documentTypeID === id
            ? {
              ...doc,
              // If editingDocName is empty or "NA", set it to null
              newDocumentTypeName:
                editingDocName.trim() === "" || editingDocName === "NA"
                  ? null
                  : editingDocName.trim(),
            }
            : doc
        )
      );
      setEditingDocId(null);
      setEditingDocName("");

      // Notify parent about change
      if (onChange) {
        onChange();
      }
    };

    // Read-only view
    if (!editMode) {
      return (
        <>
          <div className="cmo-stepper-wrapper plant-details-step">
            <div className="stepper-container">
              <form autoComplete="off">
                {isDocLoading || isCategoryLoading ? (
                  <div className={classes.loaderContainer}>
                    <CircularProgress />
                  </div>
                ) : (
                  <TableContainer
                    component={Paper}
                    style={{ minWidth: 400, marginTop: 16, marginBottom: 16 }}
                  >
                    <Table>
                      <TableHead style={{ backgroundColor: "#f9f9fc" }}>
                        <TableRow>
                          <TableCell style={{ fontSize: 16 }}>
                            Selection
                          </TableCell>
                          <TableCell style={{ fontSize: 16 }}>
                            Document Type
                          </TableCell>
                          <TableCell style={{ fontSize: 16 }}>
                            Renamed Document Type
                          </TableCell>
                          <TableCell style={{ fontSize: 16 }}>
                            Document Type ID
                          </TableCell>
                          <TableCell style={{ fontSize: 16 }}>Document Category</TableCell>
                          <TableCell style={{ fontSize: 16 }}>Mandatory In Process Workflow</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documentTypesData.map((doc) => (
                          <TableRow key={doc.documentTypeID}>
                            <TableCell>
                              <span style={{ position: "relative" }}>
                                <IOSSwitch
                                  className={classes.iosSwitch}
                                  checked={doc.activated === true}
                                  disabled={true}
                                  color="primary"
                                />
                                {doc.activated === true && (
                                  <Check className={classes.checkedSwitch} />
                                )}
                              </span>
                            </TableCell>
                            <TableCell>{doc.documentTypeName}</TableCell>
                            <TableCell>
                              {doc.newDocumentTypeName &&
                                doc.newDocumentTypeName !== "NA"
                                ? doc.newDocumentTypeName
                                : "NA"}
                            </TableCell>
                            <TableCell>{doc.documentTypeID}</TableCell>

                            <TableCell>{doc.documentCategoryName || "NA"}</TableCell>
                            {/* <TableCell>{getCategoryName(doc.documentCategoryID)}</TableCell> */}
                            <TableCell>
                              <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown}>
                                <Select
                                  value={doc.mandatoryInProcessWorkflow  ? 'Yes' : 'No'}
                                  disabled={true}
                                  displayEmpty
                                >
                                  <MenuItem value="No">No</MenuItem>
                                  <MenuItem value="Yes">Yes</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>


                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </form>
              <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="cmo-stepper-wrapper plant-details-step">
          <div className="stepper-container">
            <form autoComplete="off">
              {isDocLoading || isCategoryLoading ? (
                <div className={classes.loaderContainer}>
                  <CircularProgress />
                </div>
              ) : (
                <TableContainer
                  component={Paper}
                  style={{ minWidth: 400, marginTop: 16, marginBottom: 16 }}
                >
                  <Table>
                    <TableHead style={{ backgroundColor: "#f9f9fc" }}>
                      <TableRow>
                        <TableCell style={{ fontSize: 16 }}>
                          Selection
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Document Type
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Renamed Document Type
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Document Type ID
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Document Category
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Mandatory In Process Workflow
                        </TableCell>
                        {/* <TableCell style={{ fontSize: 16 }}>Actions</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documentTypesData.map((doc) => (
                        <TableRow key={doc.documentTypeID}>
                          <TableCell>
                            <span style={{ position: "relative" }}>
                              <IOSSwitch
                                className={classes.iosSwitch}
                                checked={doc.activated === true}
                                onChange={(e) =>
                                  handleToggle(
                                    doc.documentTypeID,
                                    e.target.checked
                                  )
                                }
                                color="primary"
                              />
                              {doc.activated === true && (
                                <Check className={classes.checkedSwitch} />
                              )}
                            </span>
                          </TableCell>
                          <TableCell>{doc.documentTypeName}</TableCell>
                          <TableCell>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                {editingDocId === doc.documentTypeID ? (
                                  <TextField
                                    value={editingDocName}
                                    onChange={(e) => {
                                      const filtered = e.target.value.replace(/[^a-zA-Z0-9\s\-/()&]/g, "");
                                      setEditingDocName(filtered);
                                    }}
                                    onBlur={(e) => {
                                      setEditingDocName(e.target.value.trim());
                                    }}
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    placeholder="Enter text here"
                                    autoFocus
                                    onFocus={(e) => {
                                      e.target.select(); // This will select all text when focused
                                    }}
                                    inputProps={{
                                      style: { fontSize: '14px' },
                                      maxLength: 255
                                    }}
                                    style={{ marginRight: 4, width: '200px' }}
                                  />
                                ) : doc.newDocumentTypeName &&
                                  doc.newDocumentTypeName !== "NA" ? (
                                  doc.newDocumentTypeName
                                ) : (
                                  "NA"
                                )}

                                {editingDocId === doc.documentTypeID ? (
                                  <div className={classes.actionButtons}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleSaveDocType(doc.documentTypeID)
                                      }
                                    >
                                      <SaveOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleCancelEdit}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                ) : (
                                  <IconButton
                                    size="normal"
                                    onClick={() =>
                                      handleEditClick(
                                        doc.documentTypeID,
                                        doc.newDocumentTypeName
                                      )
                                    }
                                  >
                                    <EditIcon fontSize="small" color="primary" />
                                  </IconButton>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{doc.documentTypeID}</TableCell>
                          <TableCell>{doc.documentCategoryName || "NA"}</TableCell>
                          <TableCell>
                            <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown}>
                              <Select
                                value={doc.mandatoryInProcessWorkflow  ? 'Yes' : 'No'}
                                onChange={(e) => handleMandatoryChange(doc.documentTypeID, e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="No">No</MenuItem>
                                <MenuItem value="Yes">Yes</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>


                          {/* <TableCell>{getCategoryName(doc.documentCategoryID)}</TableCell> */}
                          
                          {/* <TableCell>
                            {editingDocId === doc.documentTypeID ? (
                              <>
                                <Button
                                  color="primary"
                                  variant="contained"
                                  size="small"
                                  style={{
                                    minWidth: 80,
                                    padding: "6px 16px",
                                    marginRight: 8,
                                    backgroundColor: "#3f51b5",
                                    color: "white",
                                  }}
                                  onClick={() =>
                                    handleSaveDocType(doc.documentTypeID)
                                  }
                                >
                                  SAVE
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  style={{
                                    minWidth: 80,
                                    padding: "6px 16px",
                                    color: "#757575",
                                    borderColor: "#757575",
                                  }}
                                  onClick={handleCancelEdit}
                                >
                                  CANCEL
                                </Button>
                              </>
                            ) : (
                              <IconButton
                                color="primary"
                                size="small"
                                style={{ color: "#3f51b5" }}
                                onClick={() =>
                                  handleEditClick(
                                    doc.documentTypeID,
                                    doc.newDocumentTypeName
                                  )
                                }
                              >
                                <EditIcon style={{ fontSize: "1.25rem" }} />
                              </IconButton>
                            )}
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </form>
            <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />
          </div>
        </div>
      </>
    );
  }
);

export default DocumentTypesSection;
